import React from 'react';
import { useTranslation } from 'react-i18next';

import { DefaultAutocompleteOption } from '../../../../submodules/web-shared/api/autocomplete/types/DefaultAutocompleteOption';
import { BaseAutocompleteParams } from '../../../../submodules/web-shared/api/autocomplete/types/BaseAutocompleteParams';
import { useOptionsHandler } from '../../../../submodules/web-shared/elements/VirtualAutocomplete/hooks/options-handler';
import { AutocompleteApiCall } from '../../../../submodules/web-shared/api/autocomplete/types/AutocompleteApiCall';
import { AutocompleteParams } from '../../../../submodules/web-shared/api/autocomplete/types/AutocompleteParams';
import Autocomplete, { AutocompleteProps } from '.';

export interface AsyncAutocompleteProps<
  T,
  ParamsT extends BaseAutocompleteParams = BaseAutocompleteParams,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'populateValueToOptions' | 'options'> {
  apiCall: AutocompleteApiCall<ParamsT, T>;
  apiCallParams?: Omit<AutocompleteParams<ParamsT>, 'bucket_size'>;
  optionsModifier?: (options: T[]) => T[];
}

const AsyncAutocomplete = <
  T extends { id: number | string } = DefaultAutocompleteOption,
  ParamsT extends BaseAutocompleteParams = BaseAutocompleteParams,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
    apiCall,
    apiCallParams,
    optionsModifier,
    ...props
  }: AsyncAutocompleteProps<T, ParamsT, Multiple, DisableClearable, FreeSolo>) => {
  const { t } = useTranslation();

  const {
    options,
    loading,
    fetchOptionsByQuery,
    handleScroll
  } = useOptionsHandler(apiCall, apiCallParams, optionsModifier);

  const isOptionEqualToValue = (option: T, value: T) => {
    return option.id === value.id;
  };

  return (
    <Autocomplete
      {...props}
      async
      options={options || []}
      loading={loading}
      onInputChange={(_e, value, reason) => {
        if (reason !== 'reset' || props.multiple) {
          fetchOptionsByQuery(value);
        }
      }}
      onClear={() => {
        fetchOptionsByQuery('');
        if (props.onClear) props.onClear();
      }}
      onBlur={(event) => {
        fetchOptionsByQuery('');
        if (props.onBlur) props.onBlur(event);
      }}
      onOptionsListScroll={handleScroll}
      noOptionsText={t(options ? 'noOptions' : 'typeToSearch')}
      placeholder={props.placeholder || t('typeToSearch')}
      populateValueToOptions
      isOptionEqualToValue={props.isOptionEqualToValue || isOptionEqualToValue}
    />
  );
};

export default AsyncAutocomplete;

